import {
    DeleteOutlined, EditOutlined, PlusOutlined, SaveOutlined,
} from '@ant-design/icons';
import {
    Button, Card, Form, Input, Modal, Space, Table,
} from 'antd';
import { useForm } from 'antd/lib/form/Form';
import confirm from 'antd/lib/modal/confirm';
import axios from 'axios';
import moment from 'moment';
import { useEffect, useState } from 'react';
import Scrollbars from 'react-custom-scrollbars-2';

export interface IKnowledge {
    id: string;
    name: string;
    description: string;
    created_at: number;
}

const AiAgentsKnowledgePage = () => {
    const [form] = useForm();
    const [list, setList] = useState<IKnowledge[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [selected, setSelected] = useState<string>();
    const [file, setFile] = useState<{ id: string; data: { content: string; } }>();

    const onRowDeleteConfirmed = async (record: IKnowledge) => {
        setLoading(true);
        try {
            await axios({
                url: `https://ai.retechcrm.ru/api/v1/knowledge/${record.id}/delete`,
                headers: {
                    Authorization: 'Bearer sk-4838e65ed26643738c60e8daef85d025',
                },
                method: 'DELETE',
            });
        } finally {
            getList();
        }
    };

    const onDeleteClick = (record: IKnowledge) => {
        confirm({
            closable: true,
            title: 'Вы действительно хотите удалить выбранную строку?',
            onOk: () => onRowDeleteConfirmed(record),
            okType: 'danger',
            cancelText: 'Отмена',
            okText: 'Удалить',
        });
    };

    const actions = (record: IKnowledge) => (
        <Space>
            <Button
                icon={<EditOutlined />}
                onClick={() => setSelected(record.id)}
                size="small"
            />
            <Button
                icon={<DeleteOutlined />}
                onClick={() => onDeleteClick(record)}
                size="small"
            />
        </Space>
    );

    const closeModal = () => setSelected(undefined);

    const getFile = async () => {
        setLoading(true);
        const response = await axios({
            url: `https://ai.retechcrm.ru/api/v1/knowledge/${selected}/files`,
            headers: {
                Authorization: 'Bearer sk-4838e65ed26643738c60e8daef85d025',
                'Content-Type': 'multipart/form-data',
            },
        });
        if (response.status === 200 && response.data && Array.isArray(response.data.items) && response.data.items.length > 0) {
            const [fileData] = response.data.items;
            if (typeof fileData.id === 'string') {
                const fileResponse = await axios({
                    url: `https://ai.retechcrm.ru/api/v1/files/${fileData.id}`,
                    headers: {
                        Authorization: 'Bearer sk-4838e65ed26643738c60e8daef85d025',
                        'Content-Type': 'multipart/form-data',
                    },
                });
                if (fileResponse.status === 200 && fileResponse.data instanceof Object) {
                    setFile(fileResponse.data);
                }
            }
        }
        setLoading(false);
    };

    const getList = async () => {
        setLoading(true);
        const response = await axios({
            url: 'https://ai.retechcrm.ru/api/v1/knowledge/',
            headers: {
                Authorization: 'Bearer sk-4838e65ed26643738c60e8daef85d025',
            },
        });
        if (response.status === 200 && Array.isArray(response.data?.items)) {
            setList(response.data.items);
        }
        setLoading(false);
    };

    const extra = (
        <div className="card-extra-content">
            <div className="card-extra-layout-content justify-content-end">
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => setSelected('new')}
                />
            </div>
        </div>
    );

    useEffect(() => {
        getList();
    }, []);

    useEffect(() => {
        form.setFieldsValue({ content: file?.data?.content });
    }, [file]);

    useEffect(() => {
        if (typeof selected === 'string') {
            if (selected === 'new') {
                form.resetFields();
            } else {
                getFile();
                form.setFieldsValue(list.find((el) => el.id === selected));
            }
        } else {
            setFile(undefined);
        }
    }, [selected]);

    const onSaveClick = async (values: any) => {
        const record = list.find((el) => el.id === selected);
        setSaving(true);
        const response = await axios({
            url: record instanceof Object ? `https://ai.retechcrm.ru/api/v1/knowledge/${record.id}/update` : 'https://ai.retechcrm.ru/api/v1/knowledge/create',
            headers: {
                Authorization: 'Bearer sk-4838e65ed26643738c60e8daef85d025',
            },
            method: 'POST',
            data: values,
        });
        if (response.status === 200 && response.data instanceof Object) {
            if (file instanceof Object) {
                await axios({
                    url: `https://ai.retechcrm.ru/api/v1/files/${file.id}/data/content/update`,
                    headers: {
                        Authorization: 'Bearer sk-4838e65ed26643738c60e8daef85d025',
                    },
                    method: 'POST',
                    data: {
                        content: values.content,
                    },
                });
            } else {
                const formData = new FormData();
                formData.append('file', new Blob([values.content], { type: 'text/plain' }), `${values.name}.txt`);
                formData.append('metadata', JSON.stringify({
                    directory_id: null,
                    knowledge_id: response.data.id,
                }));
                await axios({
                    url: 'https://ai.retechcrm.ru/api/v1/files/',
                    headers: {
                        Authorization: 'Bearer sk-4838e65ed26643738c60e8daef85d025',
                        'Content-Type': 'multipart/form-data',
                    },
                    method: 'POST',
                    data: formData,
                });
            }
        }
        getList();
        setSaving(false);
        closeModal();
    };

    return (
        <>
            <Scrollbars autoHide className="scrollable-content">
                <Space className="w-100" direction="vertical">
                    <Card className="page-object-horizontal-margin" title="Список знаний" extra={extra}>
                        <Table
                            loading={loading}
                            bordered
                            dataSource={list}
                            columns={[{
                                dataIndex: 'name',
                                title: 'Наименование',
                            }, {
                                align: 'center',
                                dataIndex: 'created_at',
                                title: 'Дата создания',
                                render: (val) => moment.unix(val).format('DD.MM.YYYY HH:mm'),
                                width: 200,
                            }, {
                                align: 'center',
                                render: actions,
                                width: 60,
                            }]}
                            pagination={false}
                        />
                    </Card>
                </Space>
            </Scrollbars>
            <Modal
                title={selected === 'new' ? 'Новая база знаний' : 'Редактирование базы знаний'}
                visible={typeof selected === 'string'}
                onCancel={closeModal}
                footer={null}
            >
                <Form form={form} layout="vertical" onFinish={onSaveClick}>
                    <Form.Item
                        label="Наименование"
                        name="name"
                        required
                        rules={[{ required: true }]}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item
                        label="Описание"
                        name="description"
                        required
                        rules={[{ required: true }]}
                    >
                        <Input.TextArea rows={2} />
                    </Form.Item>
                    <Form.Item
                        label="Содержание"
                        name="content"
                        required
                        rules={[{ required: true }]}
                    >
                        <Input.TextArea rows={10} />
                    </Form.Item>
                    <Button
                        htmlType="submit"
                        type="primary"
                        icon={<SaveOutlined />}
                        loading={saving}
                    >
                        Сохранить
                    </Button>
                </Form>
            </Modal>
        </>
    );
};

export default AiAgentsKnowledgePage;
